import { useState, useMemo, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Search, LayoutGrid, List as ListIcon, CheckSquare, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { TemplateCard } from "@/components/TemplateCard";
import { TemplateList } from "@/components/TemplateList";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const LIBRARY_FILTERS = [
    { key: "all", label: "All templates" },
    { key: "mine", label: "My templates" },
    { key: "shared", label: "Shared with team" },
    { key: "drafts", label: "Drafts" },
];

const SORTS = [
    { key: "recent", label: "Most recent" },
    { key: "name", label: "Name A–Z" },
];

export default function LibraryPage() {
    const { user } = useAuth();
    const { templates, brands, categories, brandByKey, loaded } = useData();
    const nav = useNavigate();

    const [libFilter, setLibFilter] = useState("all");
    const [brandFilter, setBrandFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState(null);
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("recent");
    const [view, setView] = useState("card");
    const [selected, setSelected] = useState(() => new Set());
    const [publishing, setPublishing] = useState(false);
    const [confirmPublish, setConfirmPublish] = useState(false);

    const { reloadTemplates } = useData();

    const isAdmin = user.access_level === "admin";
    const meId = user.id;

    const canPublish = (t) => isAdmin || t.owner_id === meId;

    const toggleSelected = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const clearSelection = () => setSelected(new Set());

    // Clear selection when filters/search/view change (per spec).
    useEffect(() => { setSelected(new Set()); }, [libFilter, brandFilter, categoryFilter, search, view]);

    // ---- Filter chain -------------------------------------------------------
    const filtered = useMemo(() => {
        let items = templates.slice();

        if (libFilter === "mine") items = items.filter((t) => t.owner_id === meId);
        else if (libFilter === "shared") items = items.filter((t) => t.shared && !t.draft);
        else if (libFilter === "drafts") items = items.filter((t) => t.draft && (isAdmin || t.owner_id === meId));

        if (brandFilter !== "all") items = items.filter((t) => t.brand_key === brandFilter);
        if (categoryFilter) items = items.filter((t) => t.category === categoryFilter);

        if (search.trim()) {
            const q = search.toLowerCase();
            items = items.filter(
                (t) =>
                    (t.name || "").toLowerCase().includes(q) ||
                    (t.subject || "").toLowerCase().includes(q) ||
                    (t.category || "").toLowerCase().includes(q) ||
                    (t.description || "").toLowerCase().includes(q) ||
                    (t.usage_scenario || "").toLowerCase().includes(q)
            );
        }

        if (sort === "name") {
            items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        } else {
            items.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
        }
        return items;
    }, [templates, libFilter, brandFilter, categoryFilter, search, sort, meId, isAdmin]);

    // ---- Counts (respect the visibility set — my/shared/drafts) -------------
    const visibleForCounts = templates; // backend already filters by permission

    const counts = useMemo(() => {
        const all = visibleForCounts.length;
        const mine = visibleForCounts.filter((t) => t.owner_id === meId).length;
        const shared = visibleForCounts.filter((t) => t.shared && !t.draft).length;
        const drafts = visibleForCounts.filter((t) => t.draft && (isAdmin || t.owner_id === meId)).length;
        return { all, mine, shared, drafts };
    }, [visibleForCounts, meId, isAdmin]);

    const brandCounts = useMemo(() => {
        const out = { all: visibleForCounts.length };
        for (const b of brands) out[b.key] = visibleForCounts.filter((t) => t.brand_key === b.key).length;
        return out;
    }, [visibleForCounts, brands]);

    const categoryCounts = useMemo(() => {
        const out = {};
        for (const c of categories) out[c.name] = visibleForCounts.filter((t) => t.category === c.name).length;
        return out;
    }, [visibleForCounts, categories]);

    const title = isAdmin ? "All team templates" : "Your template library";
    const summary = (isAdmin ? "Admin view · " : "") +
        `${filtered.length} ${filtered.length === 1 ? "template" : "templates"} shown`;

    // Templates the current user is allowed to publish in the current filter.
    const publishable = filtered.filter(canPublish);
    const selectableIds = publishable.map((t) => t.id);
    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

    const toggleSelectAll = () => {
        if (allSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(selectableIds));
        }
    };

    const runBulkPublish = async () => {
        setPublishing(true);
        try {
            const ids = Array.from(selected);
            const r = await api.post("/templates/bulk-publish", { template_ids: ids });
            const c = r.data.counts;
            const bits = [`${c.published} published`];
            if (c.skipped_already_published) bits.push(`${c.skipped_already_published} already published`);
            if (c.skipped_no_permission) bits.push(`${c.skipped_no_permission} skipped (no permission)`);
            if (c.not_found) bits.push(`${c.not_found} not found`);
            toast.success(bits.join(" · "), { position: "bottom-center", duration: 5000 });
            await reloadTemplates();
            setConfirmPublish(false);
            clearSelection();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Publish failed",
                        { position: "bottom-center" });
        }
        setPublishing(false);
    };

    return (
        <div className="flex" style={{ minHeight: "calc(100vh - 62px)" }}>
            {/* SIDEBAR */}
            <aside
                data-testid="library-sidebar"
                className="w-72 shrink-0 border-r px-5 py-6 flex flex-col gap-6 overflow-y-auto"
                style={{ background: "#fff", borderColor: "var(--tms-border)", height: "calc(100vh - 62px)", position: "sticky", top: 62 }}
            >
                <button
                    data-testid="new-template-btn"
                    onClick={() => nav("/templates/new")}
                    className="btn-primary-green justify-center w-full"
                >
                    <Plus size={16} /> New template
                </button>

                {/* Library */}
                <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", fontWeight: 600, color: "var(--tms-muted)", textTransform: "uppercase" }} className="mb-2">
                        Library
                    </div>
                    <div className="space-y-1">
                        {LIBRARY_FILTERS.map((f) => (
                            <button
                                key={f.key}
                                onClick={() => setLibFilter(f.key)}
                                className={`tms-nav-item ${libFilter === f.key ? "is-active" : ""}`}
                                aria-pressed={libFilter === f.key}
                                data-testid={`library-filter-${f.key}`}
                            >
                                <span>{f.label}</span>
                                <span className="tms-chip-count">{counts[f.key === "all" ? "all" : f.key === "mine" ? "mine" : f.key === "shared" ? "shared" : "drafts"]}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Brand */}
                <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", fontWeight: 600, color: "var(--tms-muted)", textTransform: "uppercase" }} className="mb-2">
                        Brand
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setBrandFilter("all")}
                            className={`tms-chip ${brandFilter === "all" ? "" : ""}`}
                            style={{
                                background: brandFilter === "all" ? "var(--tms-navy)" : "#eef0f3",
                                color: brandFilter === "all" ? "#fff" : "var(--tms-navy)",
                                height: 28,
                                padding: "0 12px",
                                cursor: "pointer",
                            }}
                            data-testid="brand-filter-all"
                        >
                            All · {brandCounts.all}
                        </button>
                        {brands.map((b) => (
                            <button
                                key={b.key}
                                onClick={() => setBrandFilter(b.key)}
                                className="tms-chip"
                                style={{
                                    background: brandFilter === b.key ? "var(--tms-navy)" : "#eef0f3",
                                    color: brandFilter === b.key ? "#fff" : "var(--tms-navy)",
                                    height: 28,
                                    padding: "0 12px",
                                    cursor: "pointer",
                                }}
                                data-testid={`brand-filter-${b.key}`}
                            >
                                {b.short_code} · {brandCounts[b.key] || 0}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Categories */}
                <div>
                    <div style={{ fontSize: 11, letterSpacing: "0.14em", fontWeight: 600, color: "var(--tms-muted)", textTransform: "uppercase" }} className="mb-2">
                        Categories
                    </div>
                    <div className="space-y-1">
                        {categories.map((c) => (
                            <button
                                key={c.name}
                                onClick={() => setCategoryFilter(categoryFilter === c.name ? null : c.name)}
                                className={`tms-nav-item ${categoryFilter === c.name ? "is-active" : ""}`}
                                aria-pressed={categoryFilter === c.name}
                                data-testid={`category-filter-${c.name}`}
                            >
                                <span>{c.name}</span>
                                <span className="tms-chip-count">{categoryCounts[c.name] || 0}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </aside>

            {/* MAIN */}
            <main className="flex-1 px-8 py-8 max-w-full overflow-x-hidden">
                {/* Header row */}
                <div className="mb-6 fade-in">
                    <h1 style={{ fontSize: 28, fontWeight: 600, color: "var(--tms-navy)" }} data-testid="library-title">
                        {title}
                    </h1>
                    <p style={{ color: "var(--tms-muted)", fontSize: 13, marginTop: 4 }} data-testid="library-summary">
                        {summary}
                    </p>
                </div>

                {/* Toolbar */}
                <div className="flex flex-wrap gap-3 items-center mb-6">
                    <div className="relative flex-1 min-w-[240px] max-w-md">
                        <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--tms-faint)" }} />
                        <input
                            data-testid="library-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search name, subject, category, description…"
                            className="tms-input"
                            style={{ paddingLeft: 36 }}
                        />
                    </div>
                    <select
                        data-testid="library-sort-select"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="tms-select"
                    >
                        {SORTS.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                    <div className="tms-card flex overflow-hidden" style={{ padding: 2 }}>
                        <button
                            onClick={() => setView("card")}
                            data-testid="view-toggle-card"
                            className={view === "card" ? "btn-primary-navy" : "btn-ghost"}
                            style={{ padding: "6px 10px", borderRadius: 8, height: 36 }}
                            aria-pressed={view === "card"}
                        >
                            <LayoutGrid size={14} />
                        </button>
                        <button
                            onClick={() => setView("list")}
                            data-testid="view-toggle-list"
                            className={view === "list" ? "btn-primary-navy" : "btn-ghost"}
                            style={{ padding: "6px 10px", borderRadius: 8, height: 36 }}
                            aria-pressed={view === "list"}
                        >
                            <ListIcon size={14} />
                        </button>
                    </div>
                    {publishable.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleSelectAll}
                            className="btn-outline"
                            data-testid="library-select-all-btn"
                            style={{ height: 36 }}
                        >
                            <CheckSquare size={13} />
                            {allSelected ? "Clear selection" : `Select all (${publishable.length})`}
                        </button>
                    )}
                </div>

                {/* Results */}
                {!loaded ? (
                    <div style={{ color: "var(--tms-muted)" }} data-testid="library-loading">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="tms-card p-12 text-center" data-testid="library-empty">
                        {templates.length === 0 ? (
                            <>
                                <div style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 6 }}>
                                    Your library is empty
                                </div>
                                <div style={{ color: "var(--tms-muted)", fontSize: 14, marginBottom: 14 }}>
                                    Start by adding a category in Settings, then create your first email template.
                                </div>
                                <Link to="/templates/new" className="btn-primary-green" data-testid="library-empty-new-btn">
                                    <Plus size={14} /> New template
                                </Link>
                            </>
                        ) : (
                            <>
                                <div style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 6 }}>
                                    No templates match those filters
                                </div>
                                <div style={{ color: "var(--tms-muted)", fontSize: 14 }}>
                                    Try broadening your filters or clearing the search.
                                </div>
                            </>
                        )}
                    </div>
                ) : view === "card" ? (
                    <div
                        className="grid gap-4"
                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}
                        data-testid="library-card-grid"
                    >
                        {filtered.map((t) => (
                            <TemplateCard
                                key={t.id}
                                template={t}
                                brand={brandByKey(t.brand_key)}
                                meId={meId}
                                selectable={canPublish(t)}
                                selected={selected.has(t.id)}
                                onToggleSelected={() => toggleSelected(t.id)}
                            />
                        ))}
                    </div>
                ) : (
                    <TemplateList
                        templates={filtered}
                        brandByKey={brandByKey}
                        meId={meId}
                        canSelect={canPublish}
                        selected={selected}
                        onToggleSelected={toggleSelected}
                    />
                )}
            </main>

            {/* Sticky selection toolbar */}
            {selected.size > 0 && (
                <div
                    data-testid="bulk-toolbar"
                    style={{
                        position: "fixed",
                        left: "50%",
                        bottom: 24,
                        transform: "translateX(-50%)",
                        background: "var(--tms-navy)",
                        color: "#fff",
                        padding: "10px 16px",
                        borderRadius: 999,
                        boxShadow: "0 10px 30px rgba(15,25,45,0.25)",
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        zIndex: 50,
                        fontSize: 13,
                    }}
                >
                    <span data-testid="bulk-toolbar-count">
                        <strong>{selected.size}</strong> selected
                    </span>
                    <span style={{ opacity: 0.4 }}>·</span>
                    <button
                        onClick={() => setConfirmPublish(true)}
                        disabled={publishing}
                        data-testid="bulk-publish-btn"
                        style={{
                            background: "var(--tms-green)",
                            color: "#fff",
                            border: "none",
                            borderRadius: 999,
                            padding: "6px 14px",
                            fontWeight: 500,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            cursor: "pointer",
                        }}
                    >
                        <Send size={12} /> Publish
                    </button>
                    <button
                        onClick={clearSelection}
                        disabled={publishing}
                        data-testid="bulk-clear-btn"
                        style={{
                            background: "transparent",
                            color: "#fff",
                            border: "none",
                            fontSize: 13,
                            cursor: "pointer",
                            opacity: 0.85,
                        }}
                    >
                        Clear selection
                    </button>
                </div>
            )}

            {confirmPublish && (
                <div
                    data-testid="bulk-publish-confirm"
                    onClick={() => !publishing && setConfirmPublish(false)}
                    style={{ position: "fixed", inset: 0, background: "rgba(15,25,45,0.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="tms-card"
                        style={{ padding: 20, width: "100%", maxWidth: 440 }}
                    >
                        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--tms-navy)", marginBottom: 8 }}>
                            Share {selected.size} template{selected.size === 1 ? "" : "s"} with the whole team?
                        </div>
                        <p style={{ fontSize: 13, color: "var(--tms-muted)", lineHeight: 1.55, marginBottom: 14 }}>
                            Advisers will see them immediately. Already-published templates in your selection will be skipped safely.
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                className="btn-outline"
                                onClick={() => setConfirmPublish(false)}
                                disabled={publishing}
                                data-testid="bulk-publish-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary-green"
                                onClick={runBulkPublish}
                                disabled={publishing}
                                data-testid="bulk-publish-confirm-btn"
                            >
                                {publishing && <Loader2 size={14} className="animate-spin" />}
                                Publish {selected.size}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
